import { useEffect, useState, useMemo, useCallback } from "react";
import { usePageTitle } from "../../../hooks/useMeta";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import ReactApexChart from "react-apexcharts";
import useApi from "../../../hooks/useApi";
import {
  getRestaurantSuccessfulOrders,
  getRestaurantCalendarOrders,
} from "../../../services/orderService";
import {
  getRestaurantPartners,
  getRestaurantRatings,
  postRestaurantCustomers,
  getManagerRestaurants,
  putSwitchRestaurant,
} from "../../../services/restaurantService";
import { getRestaurantBookings } from "../../../services/bookingService";
import {
  subDays,
  subWeeks,
  subMonths,
  addMonths,
  startOfDay,
  endOfDay,
  format,
} from "date-fns";
import Card from "../../utils/Card";
import Alert from "../../utils/Alert";
import Tag from "../../utils/Tag";
import { formatDate, formatMoney } from "../../../utils/formatting";
import Button from "../../buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/pro-light-svg-icons";
import {
  faStar,
  faStarHalfAlt,
  faMoneyBill,
  faReceipt,
  faChartLine,
  faCircleCheck,
  faCircle,
} from "@fortawesome/pro-solid-svg-icons";
import {
  faCalendarCheck,
  faTriangleExclamation,
  faUserGroup,
  faUtensils,
} from "@fortawesome/pro-regular-svg-icons";
import useLangNavigate from "../../../hooks/useLangNavigate";
import { setUserRestaurants } from "../../../redux/slices/userSlice";
import Spinner from "../../utils/Spinner";
import Point from "../../utils/Point";
import useRestaurantActivationStatus from "../../../hooks/useRestaurantActivationStatus";
import { toast } from "react-toastify";

const PERIODS = [
  { key: "day", compute: () => subDays(new Date(), 1) },
  { key: "week", compute: () => subWeeks(new Date(), 1) },
  { key: "month", compute: () => subMonths(new Date(), 1) },
  { key: "sixMonths", compute: () => subMonths(new Date(), 6) },
];

const ACTIVATION_STEP_ETA = {
  banking: "10min",
  dish: "2min",
  schedules: "1min",
};

const UPCOMING_RANGE_MONTHS = 3;

const KpiCard = ({ icon, label, value, subtitle }) => (
  <Card className="overview-kpi" flat>
    <div className="overview-kpi__icon">
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="overview-kpi__content">
      <div className="overview-kpi__value">{value}</div>
      <span className="overview-kpi__label">{label}</span>
      {subtitle && <span className="overview-kpi__subtitle">{subtitle}</span>}
    </div>
  </Card>
);

const StarRating = ({ value }) => {
  if (!value) return null;
  return (
    <span className="flex align-center gap-1">
      {[...Array(Math.floor(value))].map((_, i) => (
        <FontAwesomeIcon size="xs" color="gold" key={i} icon={faStar} />
      ))}
      {value % 1 >= 0.5 && (
        <FontAwesomeIcon size="xs" color="gold" icon={faStarHalfAlt} />
      )}
    </span>
  );
};

export const RestaurantOverview = ({ tKey = "dashboard.restaurant." }) => {
  usePageTitle("restaurantOverview");
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const dispatch = useDispatch();

  const [partners, setPartners] = useState([]);
  const [ratings, setRatings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState({ existing: 0, new: 0 });
  const [bookings, setBookings] = useState([]);
  const [upcomingOrdersCount, setUpcomingOrdersCount] = useState(0);
  const [upcomingBookingsCount, setUpcomingBookingsCount] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState("month");

  const { request: getSuccessfulOrdersRequest, loading: ordersLoading } =
    useApi(getRestaurantSuccessfulOrders);
  const { request: getRatingsRequest } = useApi(getRestaurantRatings);
  const { request: getPartnersRequest } = useApi(getRestaurantPartners);
  const { request: getCustomersRequest } = useApi(postRestaurantCustomers);
  const { request: getManagerRestaurantsRequest } = useApi(
    getManagerRestaurants
  );
  const { request: getBookingsRequest } = useApi(getRestaurantBookings);
  const { request: switchRestaurantRequest } = useApi(putSwitchRestaurant);

  const restaurantId = useSelector((state) => state.user.restaurantId);
  const userId = useSelector((state) => state.user.userId);
  const restaurant = useSelector((state) =>
    state.user.restaurants?.find((r) => r.restaurantId === restaurantId)
  );
  const {
    completedRequired,
    totalRequired,
    progressPercent,
    steps: activationSteps,
    stripeUrl,
    canActivateRestaurant,
  } = useRestaurantActivationStatus(restaurantId);

  const [isRestaurantActiveLocal, setIsRestaurantActiveLocal] = useState(
    () => !!restaurant?.isActive,
  );

  useEffect(() => {
    setIsRestaurantActiveLocal(!!restaurant?.isActive);
  }, [restaurant?.isActive]);

  const dateRange = useMemo(() => {
    const now = new Date();
    const period = PERIODS.find((p) => p.key === selectedPeriod) || PERIODS[2];
    return { startDate: startOfDay(period.compute()), endDate: endOfDay(now) };
  }, [selectedPeriod]);

  // --- Derived data ---

  const ordersComposition = useMemo(() => {
    if (!orders?.length) return [];
    const grouped = orders.reduce((acc, order) => {
      ["dishes", "menus", "todaysSpecials"].forEach((category) => {
        const key =
          category === "menus"
            ? "menu"
            : category === "todaysSpecials"
              ? "todaysspecial"
              : "dish";
        order[category]?.forEach((item) => {
          const name = item[key]?.name;
          if (name) acc[name] = (acc[name] || 0) + 1;
        });
      });
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [orders]);

  const totalSales = useMemo(
    () => orders?.reduce((acc, o) => acc + (o.totalPrice || 0), 0) ?? 0,
    [orders]
  );

  const orderCount = useMemo(() => orders?.length ?? 0, [orders]);

  const averageOrderValue = useMemo(
    () => (orderCount > 0 ? totalSales / orderCount : 0),
    [totalSales, orderCount]
  );

  const averageRating = useMemo(() => {
    if (!ratings) return null;
    const ratingFields = ["ratingFood", "ratingTodaysSpecial"];
    const values = ratingFields
      .map((k) => ratings[k])
      .filter((v) => typeof v === "number" && v > 0);
    if (!values.length) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }, [ratings]);

  const salesChartData = useMemo(() => {
    if (!orders?.length) return null;
    const grouped = orders.reduce((acc, item) => {
      const date = formatDate(item.startAt.split("T")[0], false);
      acc[date] = (acc[date] || 0) + (item.totalPrice || 0);
      return acc;
    }, {});
    return {
      categories: Object.keys(grouped),
      series: Object.values(grouped),
    };
  }, [orders]);

  const pendingBookings = useMemo(
    () => bookings.filter((b) => b.status === "pending"),
    [bookings]
  );

  const confirmedBookings = useMemo(
    () => bookings.filter((b) => b.status === "confirmed"),
    [bookings]
  );

  const totalCovers = useMemo(
    () => bookings.reduce((acc, b) => acc + (b.partySize || 0), 0),
    [bookings]
  );

  const upcomingTotalCount = upcomingOrdersCount + upcomingBookingsCount;

  // --- Chart options ---

  const salesChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      xaxis: { categories: salesChartData?.categories || [] },
      plotOptions: {
        bar: {
          borderRadius: 4,
          colors: { ranges: [{ from: 0, to: 100000, color: "#1AE170" }] },
        },
      },
      tooltip: {
        y: { formatter: (val) => formatMoney(val) },
      },
    }),
    [salesChartData]
  );

  const customersChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        parentHeightOffset: 0,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "75%",
        },
      },
      xaxis: {
        categories: [t(`${tKey}customers`)],
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      grid: {
        show: false,
        padding: { left: 0, right: 0, top: -10, bottom: -10 },
      },
      colors: ["#323232", "#1AE170"],
      legend: {
        position: "bottom",
        horizontalAlign: "center",
      },
      dataLabels: { enabled: false },
      tooltip: {
        y: { formatter: (val) => `${val}` },
      },
    }),
    [t, tKey]
  );

  // --- Fetch functions ---

  const fetchOrdersRestaurant = useCallback(
    async (restaurantId, startAt, endAt) => {
      try {
        const response = await getSuccessfulOrdersRequest(
          restaurantId,
          startAt,
          endAt,
          true
        );
        setOrders(response.data);
      } catch (error) {
        console.error(error);
      }
    },
    [getSuccessfulOrdersRequest]
  );

  const fetchRatings = useCallback(
    async (restaurantId) => {
      try {
        const response = await getRatingsRequest(restaurantId);
        setRatings(response.data);
      } catch (error) {
        console.error(error);
      }
    },
    [getRatingsRequest]
  );

  const fetchPartners = useCallback(
    async (restaurantId) => {
      try {
        const response = await getPartnersRequest(restaurantId);
        setPartners(response.data);
      } catch (error) {
        console.error(error);
      }
    },
    [getPartnersRequest]
  );

  const fetchCustomers = useCallback(
    async (restaurantId, startAt, endAt) => {
      try {
        const response = await getCustomersRequest(restaurantId, startAt, endAt);
        setCustomers(response.data || { existing: 0, new: 0 });
      } catch (error) {
        console.error(error);
      }
    },
    [getCustomersRequest]
  );

  const fetchBookings = useCallback(
    async (restaurantId, startAt, endAt) => {
      try {
        const response = await getBookingsRequest(restaurantId, {
          from: startAt.toISOString(),
          to: endAt.toISOString(),
          status: "pending,confirmed",
        });
        setBookings(response.data?.data || []);
      } catch (error) {
        console.error(error);
      }
    },
    [getBookingsRequest]
  );

  const fetchManagerRestaurants = useCallback(
    async (userId) => {
      try {
        const response = await getManagerRestaurantsRequest(userId);
        dispatch(setUserRestaurants(response.data));
      } catch (error) {
        console.error(error);
      }
    },
    [getManagerRestaurantsRequest, dispatch]
  );

  const handleActivateRestaurantFromOverview = useCallback(() => {
    if (!restaurantId || !canActivateRestaurant) return;
    switchRestaurantRequest(restaurantId)
      .then((response) => {
        setIsRestaurantActiveLocal(response.data.isActive);
        fetchManagerRestaurants(userId);
        toast.success(
          t(
            `dashboard.restaurant.toasts.switchRestaurant${
              response.data.isActive ? "Active" : "Inactive"
            }`,
          ),
        );
      })
      .catch((error) => {
        toast.error(error.message);
      });
  }, [
    restaurantId,
    userId,
    canActivateRestaurant,
    switchRestaurantRequest,
    fetchManagerRestaurants,
    t,
  ]);

  // --- Effects ---

  useEffect(() => {
    fetchManagerRestaurants(userId);
    if (!restaurantId) return;
    fetchPartners(restaurantId);
    fetchRatings(restaurantId);
  }, [
    fetchManagerRestaurants,
    fetchPartners,
    fetchRatings,
    restaurantId,
    userId,
  ]);

  useEffect(() => {
    if (!restaurantId) return;
    const { startDate, endDate } = dateRange;
    fetchOrdersRestaurant(restaurantId, startDate, endDate);
    fetchCustomers(restaurantId, startDate, endDate);
    fetchBookings(restaurantId, startDate, endDate);
  }, [restaurantId, dateRange, fetchOrdersRestaurant, fetchCustomers, fetchBookings]);

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    const fromStr = format(startOfDay(new Date()), "yyyy-MM-dd");
    const toStr = format(endOfDay(addMonths(new Date(), UPCOMING_RANGE_MONTHS)), "yyyy-MM-dd");

    (async () => {
      try {
        const [ordersRes, bookingsRes] = await Promise.all([
          getRestaurantCalendarOrders(restaurantId, fromStr, toStr),
          getRestaurantBookings(restaurantId, {
            from: fromStr,
            to: toStr,
            status: "confirmed",
            limit: 500,
          }),
        ]);
        if (cancelled) return;
        const nowMs = Date.now();
        const ordList = Array.isArray(ordersRes.data) ? ordersRes.data : [];
        const bookList =
          bookingsRes.data?.data || bookingsRes.data?.rows || [];
        const oCount = ordList.filter(
          (o) => new Date(o.mealDate || o.startAt).getTime() > nowMs
        ).length;
        const bCount = bookList.filter(
          (b) => b.status === "confirmed" && new Date(b.bookingDate).getTime() > nowMs
        ).length;
        setUpcomingOrdersCount(oCount);
        setUpcomingBookingsCount(bCount);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setUpcomingOrdersCount(0);
          setUpcomingBookingsCount(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  if (ordersLoading) return <Spinner />;

  const isInactive = restaurant && !isRestaurantActiveLocal;

  return (
    <div className="dashboard-content column gap-2">
      {/* Status banner */}
      {isInactive && !canActivateRestaurant && (
        <Alert variant="pastel-light" className="overview-activation-alert">
          <div className="overview-activation-header">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{t(`${tKey}inactiveWarning`)}</span>
          </div>
          <div className="overview-activation-progress-row">
            <span className="overview-activation-progress__label">
              {t(`${tKey}onboardingProgress`)}
            </span>
            <div className="overview-activation-track">
              <div
                className="overview-activation-fill"
                style={{ width: `${progressPercent}%` }}
              />
              <span className="overview-activation-track__count">
                {completedRequired}/{totalRequired}
              </span>
            </div>
          </div>
          <div className="overview-activation-steps">
            {activationSteps.map((step) => (
              <Button
                key={step.key}
                size="xs"
                variant={step.done ? "medium" : "danger"}
                className={`overview-activation-step ${step.done ? "is-done" : "is-pending"}`}
                onClick={() => {
                  if (step.key === "banking") {
                    if (stripeUrl) {
                      window.open(stripeUrl, "_blank");
                    } else {
                      toast.warning(t("dashboard.restaurant.activationIncomplete"));
                    }
                    return;
                  }
                  navigate(step.route);
                }}
              >
                <FontAwesomeIcon
                  className="overview-activation-step__icon"
                  icon={step.done ? faCircleCheck : faCircle}
                />
                <span>{t(step.labelKey)}</span>
                {!step.done && (
                  <span className="overview-activation-step__eta">
                    {ACTIVATION_STEP_ETA[step.key]}
                  </span>
                )}
              </Button>
            ))}
          </div>
          {canActivateRestaurant && !isRestaurantActiveLocal && (
            <div className="overview-activation-cta mt-2">
              <Button
                variant="success"
                onClick={handleActivateRestaurantFromOverview}
              >
                {t("dashboard.restaurant.switchRestaurantActive")}
              </Button>
            </div>
          )}
        </Alert>
      )}

      {/* Period filter */}
      <div className="flex flex-wrap gap-1 overview-period-filters">
        {PERIODS.map((p) => (
          <Button
            key={p.key}
            size="xs"
            variant={selectedPeriod === p.key ? "primary" : "white"}
            onClick={() => setSelectedPeriod(p.key)}
          >
            {t(`${tKey}period.${p.key}`)}
          </Button>
        ))}
      </div>

      {/* KPI row */}
      <div className="flex flex-wrap gap-1 overview-kpi-row" data-tour="overview-kpi-row">
        <KpiCard
          icon={faMoneyBill}
          label={t(`${tKey}kpi.sales`)}
          value={formatMoney(totalSales)}
        />
        <KpiCard
          icon={faReceipt}
          label={t(`${tKey}kpi.upcoming`)}
          value={
            <div className="overview-kpi__triple">
              <div className="overview-kpi__triple-col">
                <span className="overview-kpi__triple-num">{upcomingTotalCount}</span>
                <span className="overview-kpi__triple-lbl">{t(`${tKey}kpi.upcomingTotal`)}</span>
              </div>
              <span className="overview-kpi__triple-sep" aria-hidden>
                ·
              </span>
              <div className="overview-kpi__triple-col">
                <span className="overview-kpi__triple-num">{upcomingOrdersCount}</span>
                <span className="overview-kpi__triple-lbl">{t(`${tKey}kpi.upcomingOrders`)}</span>
              </div>
              <span className="overview-kpi__triple-sep" aria-hidden>
                ·
              </span>
              <div className="overview-kpi__triple-col">
                <span className="overview-kpi__triple-num">{upcomingBookingsCount}</span>
                <span className="overview-kpi__triple-lbl">{t(`${tKey}kpi.upcomingBookings`)}</span>
              </div>
            </div>
          }
        />
        <KpiCard
          icon={faChartLine}
          label={t(`${tKey}kpi.avgOrder`)}
          value={formatMoney(averageOrderValue)}
        />
        <KpiCard
          icon={faStar}
          label={t(`${tKey}kpi.rating`)}
          value={
            averageRating !== null ? (
              <span className="flex align-center gap-1">
                {averageRating.toFixed(1)}
                <StarRating value={averageRating} />
              </span>
            ) : (
              t(`${tKey}kpi.noRating`)
            )
          }
        />
      </div>

      {/* Sales chart */}
      <Card
        flat
        className="resto-overview-item"
        title={t(`${tKey}sales`)}
        data-tour="overview-sales-card"
      >
        {salesChartData ? (
          <ReactApexChart
            height="250px"
            options={salesChartOptions}
            series={[
              { name: t(`${tKey}salesShort`), data: salesChartData.series },
            ]}
            type="bar"
          />
        ) : (
          <div className="flex justify-center p-3 italic text-light">
            {t("utils.errors.noData.title")}
          </div>
        )}
      </Card>

      {/* Bookings + Best sellers */}
      <div className="flex flex-wrap gap-1 resto-overview-cards-row">
        <Card
          flat
          className="col-6 resto-overview-item"
          title={t(`${tKey}bookings`)}
          icon={faCalendarCheck}
          data-tour="overview-bookings-card"
          headerActions={
            <Button
              className="text-light"
              link
              onClick={() => navigate("dashboard/bookings")}
            >
              {t(`${tKey}viewAllBookings`)}
              <FontAwesomeIcon className="ml-2" icon={faArrowRightLong} />
            </Button>
          }
        >
          <div className="flex gap-2 mb-2">
            <div className="flex align-center gap-1">
              <Point variant="warning" />
              <span>
                {t(`${tKey}pendingBookings`, {
                  count: pendingBookings.length,
                })}
              </span>
            </div>
            <div className="flex align-center gap-1">
              <Point variant="success" />
              <span>
                {t(`${tKey}confirmedBookings`, {
                  count: confirmedBookings.length,
                })}
              </span>
            </div>
          </div>
          <div className="flex align-center gap-1">
            <FontAwesomeIcon icon={faUserGroup} className="text-light" />
            <span>
              {t(`${tKey}expectedCovers`, { count: totalCovers })}
            </span>
          </div>
          {bookings.length > 0 && (
            <div className="mt-2">
              {bookings.slice(0, 4).map((booking, i) => (
                <div key={booking.bookingId} className="flex gap-2 my-1 align-center">
                  <Tag variant="pastel-light" round text={i + 1} />
                  <span>{booking.guestName}</span>
                  <span className="text-light">
                    {format(new Date(booking.bookingDate), "HH:mm")}
                  </span>
                  <span className="ml-auto">
                    <FontAwesomeIcon icon={faUserGroup} size="xs" className="mr-1" />
                    {booking.partySize}
                  </span>
                </div>
              ))}
            </div>
          )}
          {!bookings.length && (
            <div className="flex justify-center p-3 italic text-light">
              {t(`${tKey}noBookings`)}
            </div>
          )}
        </Card>

        <Card
          flat
          className="col resto-overview-item"
          title={t(`${tKey}items`)}
          icon={faUtensils}
          data-tour="overview-items-card"
          headerActions={
            <span className="text-light">
              {ordersComposition.reduce((acc, i) => acc + i.count, 0)}{" "}
              {t(`${tKey}itemsSold`)}
            </span>
          }
        >
          {ordersComposition.length ? (
            ordersComposition.slice(0, 6).map((item, index) => (
              <div key={index} className="flex gap-2 my-1 align-center">
                <Tag variant="pastel-light" round text={index + 1} />
                <span>{item.name}</span>
                <span className="ml-auto bold">{item.count}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-center p-3 italic text-light">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </Card>
      </div>

      {/* Customers + Partners + Ratings */}
      <div className="flex flex-wrap gap-1 resto-overview-cards-row">
        <Card
          flat
          className="col-4 resto-overview-item"
          title={t(`${tKey}customers`)}
          data-tour="overview-customers-card"
          headerActions={
            <span className="bold">
              {(customers.existing || 0) + (customers.new || 0)}
            </span>
          }
        >
          {customers.existing || customers.new ? (
            <ReactApexChart
              width="100%"
              height="120px"
              options={customersChartOptions}
              series={[
                {
                  name: t(`${tKey}existingCustomers`, {
                    count: customers.existing || 0,
                  }),
                  data: [customers.existing || 0],
                },
                {
                  name: t(`${tKey}newCustomers`, {
                    count: customers.new || 0,
                  }),
                  data: [customers.new || 0],
                },
              ]}
              type="bar"
            />
          ) : (
            <div className="flex justify-center p-3 italic text-light">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </Card>

        <Card
          flat
          className="col resto-overview-item"
          title={t(`${tKey}ratings`)}
          data-tour="overview-ratings-card"
          headerActions={
            <span className="bold">{ratings?.ratingCount ?? 0}</span>
          }
        >
          {ratings ? (
            ["ratingFood", "ratingTodaysSpecial"].map(
              (key, index) => (
                <div key={key} className="flex gap-2 my-1 align-center">
                  <Tag variant="pastel-light" round text={index + 1} />
                  <span>{t(`utils.ratings.${key}`)}</span>
                  <span className="ml-auto">
                    {ratings[key] ? (
                      <StarRating value={ratings[key]} />
                    ) : (
                      <span className="italic text-light">
                        {t(`utils.ratings.notYet`)}
                      </span>
                    )}
                  </span>
                </div>
              )
            )
          ) : (
            <div className="flex justify-center p-3 italic text-light">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </Card>

        <Card
          flat
          className="col-3 resto-overview-item"
          title={t(`${tKey}partners`)}
          data-tour="overview-partners-card"
          headerActions={<span className="bold">{partners.length}</span>}
        >
          {partners.length ? (
            <>
              {partners.slice(0, 4).map((partner, index) => (
                <div key={index} className="flex gap-2 my-1 align-center">
                  <Tag variant="pastel-light" round text={index + 1} />
                  <span>{partner.name}</span>
                </div>
              ))}
              <div className="mt-auto flex justify-end">
                <Button
                  className="text-light"
                  link
                  onClick={() => navigate("dashboard/partners")}
                >
                  {t(`${tKey}viewAllPartners`)}
                  <FontAwesomeIcon className="ml-2" icon={faArrowRightLong} />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-center p-3 italic text-light">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default RestaurantOverview;
